import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { PhonicsSound, useGetPhonicsSoundsQuery } from '@/api/types';
import { useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import {
  AudioPlayer,
  CdnMedia,
  CanvasTrace,
  RoundPlayButton,
  Button,
} from '@/components';
import { getSoundExtraData } from '@/utils/soundsData';
import { WhitePanel } from '@/components/LessonPlayer/components/WhitePanel';

interface ManageAdminSymbolsSoundProps {
  sound: string;
  includeImage?: boolean;
  includeShapes?: boolean;
}

export const ManageAdminSymbolsSound = ({
  sound,
  includeImage = true,
  includeShapes = false,
}: ManageAdminSymbolsSoundProps) => {
  const traceRef = useRef<CanvasTrace>(null);

  const playerSoundRef = useRef<AudioPlayer>(null);
  const playerExemplarRef = useRef<AudioPlayer>(null);

  const mediaSoundUrl = useAssetCdn(
    sound,
    SymbolType.Sound,
    CdnObjectType.Audio,
  );
  const mediaExemplarUrl = useAssetCdn(
    sound,
    SymbolType.Sound,
    CdnObjectType.Audio,
    true,
  );

  const [soundPlayerReady, setSoundPlayerReady] = useState(false);
  const [exemplarPlayerReady, setExemplarPlayerReady] = useState(false);

  const [soundObject, setSoundObject] = useState<PhonicsSound | undefined>(
    undefined,
  );

  const { data: soundsData } = useGetPhonicsSoundsQuery();

  const {
    arrows: arrowsPath,
    lines: linesPath,
    lineYOffset,
  } = useMemo(() => {
    if (!soundObject) {
      return {
        arrows: undefined,
        lines: undefined,
        lineYOffset: 0,
      };
    }

    return getSoundExtraData(soundObject.name as any);
  }, [soundObject]);

  useEffect(() => {
    if (!soundsData) {
      return;
    }

    setSoundObject(soundsData.getPhonicsSounds.find((s) => s.name === sound));
  }, [soundsData, sound]);

  const soundData = useMemo(() => getSoundExtraData(sound as any), [sound]);

  return (
    <>
      <h6 className="font-bold">Sound: {sound}</h6>

      {includeImage && (
        <CdnMedia
          className="w-full h-auto"
          symbol={sound}
          symbolType={SymbolType.Sound}
          objectType={CdnObjectType.Image}
          width={280}
          height={280}
        />
      )}

      {includeShapes && soundObject && (
        <>
          <WhitePanel className="my-6" mode="letter">
            <div
              className={clsx(
                soundData.hasTail && sound !== 'f' && '-mt-24',
                sound == 'f' && '-mt-10',
                sound.length > 2 && 'text-9xl',
              )}
            >
              {sound}
            </div>
          </WhitePanel>

          <Button
            style="secondary"
            onClick={() => {
              traceRef.current?.reset();
            }}
          >
            Reset
          </Button>

          <div className="w-80 border-2 border-black border-solid">
            <CanvasTrace
              ref={traceRef}
              className="w-80 h-80"
              drawWidth={320}
              drawHeight={320}
              traceBasis={soundObject.referencePath || undefined}
              traceGuide={soundObject.guidePath || undefined}
              additionalPrefixPaths={[
                ...(linesPath && linesPath !== ''
                  ? [
                      {
                        path: linesPath,
                        color: '#bebebe',
                        strokeWidth: 0,
                        yOffset: lineYOffset || 0,
                      },
                    ]
                  : []),
              ]}
              additionalSuffixPaths={[
                ...(arrowsPath && arrowsPath !== ''
                  ? [
                      {
                        path: arrowsPath,
                        strokeWidth: 0.7,
                      },
                    ]
                  : []),
              ]}
              seed="topRight"
              showScore={false}
              includeBgOnLines={false}
            />
          </div>
        </>
      )}

      {mediaSoundUrl && mediaExemplarUrl ? (
        <div className="flex gap-3">
          <AudioPlayer
            ref={playerSoundRef}
            src={mediaSoundUrl}
            onReadySrc={() => setSoundPlayerReady(true)}
          />

          <RoundPlayButton
            onClick={() => {
              playerSoundRef.current?.play();
            }}
            size="sm"
            disabled={!soundPlayerReady}
          />

          <AudioPlayer
            ref={playerExemplarRef}
            src={mediaExemplarUrl}
            onReadySrc={() => setExemplarPlayerReady(true)}
          />

          <RoundPlayButton
            onClick={() => {
              playerExemplarRef.current?.play();
            }}
            size="sm"
            color="green"
            disabled={!exemplarPlayerReady}
          />
        </div>
      ) : (
        <p>Sound not found</p>
      )}
    </>
  );
};
